// src/lib/db.ts
import Dexie from 'dexie';
import { auth } from './auth';
import {paths, api} from './api';

export interface CacheItem {
  key: string;
  entityPath: string;
  ids: number[];
  data: unknown;
  etag: string;
  timestamp: number;
  // New fields for refresh
  requestPath: paths;
  filters?: Record<string, unknown>;
  projection?: string[];
  pathReplace?: Record<string, string>;
  query?: Record<string, string>;
  dtoSchema?: string; // Store schema identifier (optional)
}

export interface EntityCacheMap {
  entityId: number;
  cacheKey: string;
}

const userDbs = new Map<string, Dexie>();

export async function getUserDb(): Promise<Dexie> {
  const user = await auth.user;
  if (!user?.profile?.sub) throw new Error('User not authenticated');
  
  const userId = user.profile.sub;
  const dbName = `SecureCache_${userId}`;

  if (!userDbs.has(dbName)) {
    const db = new Dexie(dbName);
    db.version(1).stores({
      cache: '&key, etag, timestamp',
      entityCacheMap: '&[entityId+cacheKey]'
    });
    userDbs.set(dbName, db);
  }

  return userDbs.get(dbName)!;
}

export async function clearUserDb(): Promise<void> {
  const user = await auth.user;
  const userId = user?.profile?.sub;
  if (!userId) return;

  const dbName = `SecureCache_${userId}`;
  await Dexie.delete(dbName);
  userDbs.delete(dbName);
}

// export async function invalidate(prefix : string) : Promise<void> {
//   const db = await getUserDb();
//   await db.table('cache').where("key").startsWith(prefix).delete();

// }

export async function invalidateByEntities(entityIds: number[]): Promise<void> {
  const db = await getUserDb();
  
  // Get composite keys [entityId, cacheKey]
  const cacheKeys = await db.table('entityCacheMap')
    .where('entityId').anyOf(entityIds)
    .primaryKeys() as Array<[number, string]>; // Optional cast for clarity
  
  // Extract unique cache keys
  const uniqueCacheKeys = [...new Set(
    cacheKeys.map(([, key]: [number, string]) => key) // Explicit tuple typing
  )];
  
  // Delete affected cache entries and mappings
  await db.table('cache').where('key').anyOf(uniqueCacheKeys).delete();
  await db.table('entityCacheMap').where('entityId').anyOf(entityIds).delete();

  uniqueCacheKeys.forEach(key => api.refreshCacheEntry(key));
}